import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, LayoutDashboard, ScrollText, FileText, Folder, ChevronDown, PanelLeft, Palette } from 'lucide-react';

const Sidebar = ({
  sidebarOpen,
  setSidebarOpen,
  activeService,
  setActiveService,
  synopsisExpanded,
  setSynopsisExpanded,
  keywordExpanded,
  setKeywordExpanded,
  transcriptionExpanded,
  setTranscriptionExpanded,
  setShowProjectDetailPage,
  setSelectedProject,
  setSynopsisProjectDetails,
  user,
}) => {
  const location = useLocation();

  const getActiveServiceFromPath = (pathname) => {
    if (pathname.startsWith('/synopsis/projects')) return 'synopsis-projects';
    if (pathname.startsWith('/keyword/projects')) return 'keyword-projects';
    if (pathname.startsWith('/transcription/projects')) return 'transcription-projects';
    if (pathname === '/synopsis') return 'synopsis';
    if (pathname === '/keyword') return 'keyword';
    if (pathname === '/transcription') return 'transcription';
    if (pathname === '/dashboard' || pathname === '/') return 'dashboard';
    return 'dashboard';
  };
  
  const currentActiveService = getActiveServiceFromPath(location.pathname);
  
  const handleServiceChange = (service) => {
    setActiveService(service);
    setShowProjectDetailPage(false);
    setSelectedProject(null);
    setSynopsisProjectDetails(null);
  };

  return (
    <div className={`${sidebarOpen ? 'w-44 sm:w-48 md:w-52' : 'w-12 sm:w-14'} flex flex-col shadow-lg transition-all duration-300 ease-in-out flex-shrink-0 h-full relative z-10`}>
      {/* Sidebar Header */}
      <div className="p-2 sm:p-2.5 border-b border-gray-200 dark:border-neutral-800 flex items-center justify-between transition-all duration-300 h-11">
        {sidebarOpen && <div className="flex-1" />}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="pl-3 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-900 transition-colors flex-shrink-0"
          aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
        >
          <PanelLeft
            className={`w-3.5 h-3.5 text-gray-600 dark:text-neutral-400 transition-transform duration-300 ease-in-out ${!sidebarOpen ? 'rotate-180' : ''}`}
          />
        </button>
      </div>
      
      {/* Navigation Menu */}
      <nav className={`flex-1 ${sidebarOpen ? 'p-1.5 sm:p-2' : 'p-1'} transition-all duration-300 overflow-y-auto text-xs`}>
        <div className="space-y-1">
          {/* Dashboard */}
          <Link
            to="/dashboard"
            onClick={() => handleServiceChange('dashboard')}
            className={`w-full flex items-center ${sidebarOpen ? 'px-2.5 py-1.5' : 'px-1.5 py-1.5 justify-center'} rounded-lg text-left transition-all duration-300 ${
              currentActiveService === 'dashboard' 
                ? 'bg-gray-100 dark:bg-neutral-900 text-gray-900 dark:text-neutral-100' 
                : 'text-gray-600 dark:text-neutral-400 hover:bg-gray-50 dark:hover:bg-neutral-900 hover:text-gray-900 dark:hover:text-gray-100'
            }`}
            title={!sidebarOpen ? "Dashboard" : ""}
          >
            <LayoutDashboard className={`w-3.5 h-3.5 ${sidebarOpen ? 'mr-2.5' : ''} flex-shrink-0`} />
            {sidebarOpen && <span className="truncate">Dashboard</span>}
          </Link>

          {/* Transcription Section */}
          <div>
            <div className="flex items-center">
              <Link
                to="/transcription"
                onClick={() => {
                  handleServiceChange('transcription');
                  setTranscriptionExpanded(!transcriptionExpanded);
                }}
                className={`flex-1 flex items-center ${sidebarOpen ? 'px-2.5 py-1.5' : 'px-1.5 py-1.5 justify-center'} rounded-lg text-left transition-all duration-300 ${
                  currentActiveService === 'transcription' || currentActiveService === 'transcription-projects'
                    ? 'bg-gray-100 dark:bg-neutral-900 text-gray-900 dark:text-neutral-100'
                    : 'text-gray-600 dark:text-neutral-400 hover:bg-gray-50 dark:hover:bg-neutral-900 hover:text-gray-900 dark:hover:text-gray-100'
                }`}
                title={!sidebarOpen ? "Transcription" : ""}
              >
                <FileText className={`w-3.5 h-3.5 ${sidebarOpen ? 'mr-2.5' : ''} flex-shrink-0`} />
                {sidebarOpen && <span className="flex-1 truncate">Transcription</span>}
                {sidebarOpen && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setTranscriptionExpanded(!transcriptionExpanded);
                    }}
                  >
                    {transcriptionExpanded ? (
                      <ChevronDown className="w-3 h-3 text-gray-600 dark:text-neutral-400" />
                    ) : (
                      <ChevronRight className="w-3 h-3 text-gray-600 dark:text-neutral-400" />
                    )}
                  </button>
                )}
              </Link>
            </div>
            {transcriptionExpanded && (
              <div className={`${sidebarOpen ? 'ml-4 mt-1' : 'mt-1'} space-y-1`}>
                <Link
                  to="/transcription/projects"
                  onClick={() => handleServiceChange('transcription-projects')}
                  className={`w-full flex items-center ${sidebarOpen ? 'px-2.5 py-1' : 'px-1.5 py-1 justify-center'} rounded-lg text-left transition-all duration-300 ${
                    currentActiveService === 'transcription-projects'
                      ? 'bg-gray-100 dark:bg-neutral-900 text-gray-900 dark:text-neutral-100'
                      : 'text-gray-600 dark:text-neutral-400 hover:bg-gray-50 dark:hover:bg-neutral-900 hover:text-gray-900 dark:hover:text-gray-100'
                  }`}
                  title={!sidebarOpen ? "Transcription Projects" : ""}
                >
                  <Folder className={`w-3 h-3 ${sidebarOpen ? 'mr-2.5' : 'ml-2.5'} flex-shrink-0`} />
                  {sidebarOpen && <span className="truncate">Projects</span>}
                </Link>
              </div>
            )}
          </div>

          {/* Synopsis Generation Section */}
          <div>
            <div className="flex items-center">
              <Link
                to="/synopsis"
                onClick={() => {
                  handleServiceChange('synopsis');
                  setSynopsisExpanded(!synopsisExpanded);
                }}
                className={`flex-1 flex items-center ${sidebarOpen ? 'px-2.5 py-1.5' : 'px-1.5 py-1.5 justify-center'} rounded-lg text-left transition-all duration-300 ${
                  currentActiveService === 'synopsis' || currentActiveService === 'synopsis-projects'
                    ? 'bg-gray-100 dark:bg-neutral-900 text-gray-900 dark:text-neutral-100' 
                    : 'text-gray-600 dark:text-neutral-400 hover:bg-gray-50 dark:hover:bg-neutral-900 hover:text-gray-900 dark:hover:text-gray-100'
                }`}
                title={!sidebarOpen ? "Synopsis Generation" : ""}
              >
                <ScrollText className={`w-3.5 h-3.5 ${sidebarOpen ? 'mr-2.5' : ''} flex-shrink-0`} />
                {sidebarOpen && <span className="flex-1 truncate">Synopsis Generation</span>}
                {sidebarOpen && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSynopsisExpanded(!synopsisExpanded);
                     }}
                   >
                     {synopsisExpanded ? (
                       <ChevronDown className="w-3 h-3 text-gray-600 dark:text-neutral-400" />
                     ) : (
                       <ChevronRight className="w-3 h-3 text-gray-600 dark:text-neutral-400" />
                     )}
                  </button>
                )}
              </Link>
              
            </div>
          
            {/* Synopsis Submenu */}
            {synopsisExpanded && (
              <div className={`${sidebarOpen ? 'ml-4 mt-1' : 'mt-1'} space-y-1`}>
                <Link
                  to="/synopsis/projects"
                  onClick={() => handleServiceChange('synopsis-projects')}
                  className={`w-full flex items-center ${sidebarOpen ? 'px-2.5 py-1' : 'px-1.5 py-1 justify-center'} rounded-lg text-left transition-all duration-300 ${
                    currentActiveService === 'synopsis-projects' 
                      ? 'bg-gray-100 dark:bg-neutral-900 text-gray-900 dark:text-neutral-100' 
                      : 'text-gray-600 dark:text-neutral-400 hover:bg-gray-50 dark:hover:bg-neutral-900 hover:text-gray-900 dark:hover:text-gray-100'
                  }`}
                  title={!sidebarOpen ? "Synopsis Projects" : ""}
                >
                  <Folder className={`w-3 h-3 ${sidebarOpen ? 'mr-2.5' : 'ml-2.5'} flex-shrink-0`} />
                  {sidebarOpen && <span className="truncate">Projects</span>}
                </Link>
              </div>
            )}
          </div>

          {/* Keyword Generation Section - commented out
          <div>
            <div className="flex items-center">
              <Link
                to="/keyword"
                onClick={() => {
                  handleServiceChange('keyword');
                  setKeywordExpanded(!keywordExpanded);
                }}
                className={`flex-1 flex items-center ${sidebarOpen ? 'px-3 py-2' : 'px-2 py-2 justify-center'} rounded-lg text-left transition-all duration-300 ${
                  currentActiveService === 'keyword' || currentActiveService === 'keyword-projects'
                    ? 'bg-gray-100 dark:bg-neutral-900 text-gray-900 dark:text-neutral-100' 
                    : 'text-gray-600 dark:text-neutral-400 hover:bg-gray-50 dark:hover:bg-neutral-900 hover:text-gray-900 dark:hover:text-gray-100'
                }`}
                title={!sidebarOpen ? "Keyword Generation" : ""}
              >
                <KeyRound className={`w-4 h-4 ${sidebarOpen ? 'mr-3' : ''} flex-shrink-0`} />
                {sidebarOpen && <span className="flex-1 truncate">Keyword Generation</span>}
                {sidebarOpen && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setKeywordExpanded(!keywordExpanded);
                     }}
                   >
                     {keywordExpanded ? (
                       <ChevronDown className="w-3.5 h-3.5 text-gray-600 dark:text-neutral-400" />
                     ) : (
                       <ChevronRight className="w-3.5 h-3.5 text-gray-600 dark:text-neutral-400" />
                     )}
                  </button>
                )}
              </Link>
              
            </div>
          
            Keyword Submenu
            {keywordExpanded && (
              <div className={`${sidebarOpen ? 'ml-4 mt-1' : 'mt-1'} space-y-1`}>
                <Link
                  to="/keyword/projects"
                  onClick={() => handleServiceChange('keyword-projects')}
                  className={`w-full flex items-center ${sidebarOpen ? 'px-3 py-1.5' : 'px-2 py-1.5 justify-center'} rounded-lg text-left transition-all duration-300 ${
                    currentActiveService === 'keyword-projects' 
                      ? 'bg-gray-100 dark:bg-neutral-900 text-gray-900 dark:text-neutral-100' 
                      : 'text-gray-600 dark:text-neutral-400 hover:bg-gray-50 dark:hover:bg-neutral-900 hover:text-gray-900 dark:hover:text-gray-100'
                  }`}
                  title={!sidebarOpen ? "Keyword Projects" : ""}
                >
                  <Folder className={`w-3.5 h-3.5 ${sidebarOpen ? 'mr-3' : ''} flex-shrink-0`} />
                  {sidebarOpen && <span className="truncate">Projects</span>}
                </Link>
              </div>
            )}
          </div>
          */}
        </div>
      </nav>
      
      {/* User Info */}
      <div className={`${sidebarOpen ? 'p-1.5 sm:p-2' : 'p-1.5'} border-t border-gray-200 dark:border-neutral-800 transition-all duration-300 flex-shrink-0`}>
        <div className={`flex items-center ${sidebarOpen ? 'space-x-1.5' : 'justify-center'}`}>
          <div className="w-6 h-6 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-[10px] font-medium text-gray-700 dark:text-neutral-300">
              {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
            </span>
          </div>
          {sidebarOpen && (
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-gray-900 dark:text-neutral-100 truncate">
                {user?.name || user?.email}
              </p>
              <p className="text-[10px] text-gray-500 dark:text-neutral-400 truncate">
                {user?.email}
              </p>
            </div>
          )}
        </div>
        <Link
          to="/design-system"
          className={`mt-1.5 w-full flex items-center text-xs ${sidebarOpen ? 'px-2.5 py-1.5' : 'px-1.5 py-1.5 justify-center'} rounded-lg text-left transition-all duration-300 text-gray-600 dark:text-neutral-400 hover:bg-gray-50 dark:hover:bg-neutral-900 hover:text-gray-900 dark:hover:text-gray-100 ${
            location.pathname === '/design-system'
              ? 'bg-gray-100 dark:bg-neutral-900 text-gray-900 dark:text-neutral-100'
              : ''
          }`}
          title={!sidebarOpen ? 'Design System' : ''}
        >
          <Palette className={`w-3 h-3 ${sidebarOpen ? 'mr-2.5' : ''} flex-shrink-0`} />
          {sidebarOpen && <span className="truncate">Design System</span>}
        </Link>
      </div>
    </div>
  );
};

export default Sidebar;

